import React, { FocusEventHandler } from 'react';

import classNames from 'classnames';

import { AmbaInputPropsType, AmbaInputTypes } from '@/models/globalModels';

import Icon from './Icon';

const AmbaInput = React.forwardRef<HTMLInputElement, AmbaInputPropsType>((props, externalRef) => {
  const {
    id,
    name,
    ariaDescribedBy,
    type = AmbaInputTypes.TEXT,
    onChange,
    onFocus,
    onBlur,
    disabled = false,
    value = '',
    extraClass = '',
    icon,
    labelText,
    extraLabelClass = '',
    extraIconClass = '',
    autoComplete = 'off',
    hasValidation = false,
    hasLiveValidation = false,
    placeHolder,
    validationMessage = '',
    validator,
    isRequired = false,
    onValidationFail,
    onValidationSuccess,
    isValidField,
  } = props;
  const [isValid, setIsValid] = React.useState<boolean>(isValidField || false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isHashed, setIsHashed] = React.useState(type === AmbaInputTypes.PASSWORD);
  const ref = React.useRef<HTMLInputElement>(null);

  const isInvalid = validationMessage ? 'is-invalid' : '';
  const validationIcon = isValid ? 'is-valid' : 'is-invalid';

  const getInputElement = () => {
    if (externalRef && typeof externalRef !== 'function') {
      return externalRef.current;
    }
    return ref.current;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true);
    if (onChange) onChange(e);
    if (validator && hasLiveValidation) {
      const isInputValid = validator(e.target.value);
      if (!isInputValid && onValidationFail) onValidationFail();
      else if (isInputValid && onValidationSuccess) onValidationSuccess();
      setIsValid(isInputValid);
    }
  };

  const handleFocus: FocusEventHandler = (e) => {
    if (type === AmbaInputTypes.DATE) {
      getInputElement()?.showPicker();
    }

    onFocus?.(e);
  };

  // updates field validation UI status if new validation message is set
  React.useEffect(() => {
    if (validationMessage) {
      setIsValid(false);
    } else if (!validationMessage) {
      setIsValid(true);
    }
  }, [validationMessage]);

  React.useEffect(() => {
    if (isValidField !== undefined) {
      setIsValid(isValidField);
    }
  }, [isValidField]);

  return (
    <div className={`AmbaInput ${extraClass}`}>
      {labelText && (
        <label className={`f-15 f-roboto-semiBold ${extraLabelClass}`} htmlFor={id}>
          {isRequired ? labelText.concat(' *') : labelText}
        </label>
      )}
      <div
        className={classNames(
          'inputContainer',
          icon && 'iconInputBox',
          hasValidation && 'has-validation',
          type === AmbaInputTypes.PASSWORD && 'passwordInputBox',
        )}
      >
        {icon && <Icon className={extraIconClass} icon={icon} />}
        <input
          aria-describedby={ariaDescribedBy}
          autoComplete={autoComplete || 'off'}
          className={`form-control input-wrapper f-14 f-roboto-semiBold ${hasLiveValidation && isDirty ? validationIcon : isInvalid}`}
          disabled={disabled}
          id={id}
          name={name}
          onBlur={onBlur}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
          onFocus={handleFocus}
          placeholder={placeHolder}
          ref={externalRef || ref}
          type={isHashed ? 'password' : type === AmbaInputTypes.PASSWORD ? 'text' : type}
          value={value ? value : ''}
        />
        {type === AmbaInputTypes.PASSWORD && (
          <button
            type="button"
            onClick={(e) => {
              setIsHashed(!isHashed);
              getInputElement()?.focus();
            }}
          >
            <Icon className={extraIconClass} icon={isHashed ? 'eye' : 'eye-slash'} />
          </button>
        )}
      </div>
      <div className={`invalid-feedback ${validationMessage ? 'd-block' : ''}`}>{validationMessage}</div>
    </div>
  );
});
export default AmbaInput;
